import CardStack, { type StackCard } from '../components/CardStack';
import { showConfirm } from '../components/ConfirmDialog';
import { useEffect, useMemo, useState, useCallback, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  apiGetTeam, apiGetTeamMember, apiDeleteTeamMember, apiGetExpiringDocs,
  apiGetTeamDuplicates, apiGetTeamNotDuplicates, apiMergeTeamMembers,
  apiMarkTeamNotDuplicate, apiUndoTeamNotDuplicate, apiDismissDuplicate,
  apiReorderTeam,
  type TeamMemberDTO, type ExpiringDoc, type TeamDuplicateGroup,
} from '../api';
import { buildPersonDeleteCopy, confirmPersonnelDelete } from '../lib/deletePersonnel';
import { useProfileContext } from '../hooks/useProfileContext';
import { useAuth } from '../context/AuthContext';
import { reportSaveError } from '../lib/saveStatus';
import { useWriteGate } from '../lib/writeGate';
import PageHeader from '../components/PageHeader';
import ActivityPanel from '../components/ActivityPanel';
import { withPageIcon } from '../lib/uiIcons';

const CATEGORY_COLORS: Record<string, string> = {
  musician: '#00d47e',
  crew: '#ff9f43',
  'tour manager': '#ff9f43',
  lights: '#feca57',
  foh: '#54a0ff',
  mon: '#5f27cd',
  driver: '#a0a0b0',
  other: '#a0a0b0',
};

const TAG_ORDER = ['musician', 'crew'];

/** Base-role filters for the team list (main/session/guest). */
const BASE_ROLE_ORDER = ['main', 'session', 'guest'];

function tagPillStyle(active: boolean): CSSProperties {
  return {
    border: '1px solid var(--border)',
    borderRadius: 999,
    padding: '4px 12px',
    fontSize: '0.82rem',
    cursor: 'pointer',
    background: active ? 'var(--accent)' : 'transparent',
    color: active ? 'var(--on-accent)' : 'var(--text-dim)',
  };
}

function daysUntil(date?: string): number | null {
  if (!date) return null;
  const d = new Date(date).getTime();
  if (Number.isNaN(d)) return null;
  return Math.ceil((d - Date.now()) / 86400000);
}

export default function TeamPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeProfileId, profiles } = useProfileContext();
  const { user: authUser } = useAuth();
  // ROSTER VISIBILITY (2026-10-07, user decision): the Team page carries
  // identity data (legal names, DOB, nationality, address, emergency contact,
  // passports, visas, diet). Only a band MANAGER (owner/editor, superadmin
  // included) sees the whole roster — everyone else sees their OWN card, and
  // edits it from Account → My Team card.
  //
  // NOTE: this is deliberately the BAND ROLE, not `writeGate.canWrite` — a
  // manager whose account carries the global `read-only` ceiling still READS
  // the roster (it is a read) while the server refuses their writes.
  const myBandRole = profiles.find((p) => p.id === activeProfileId)?.my_role;
  // The delegated `team.edit` capability (2026-10-08) opens the registry too:
  // its holder manages the people list, which is unusable without seeing it.
  // Capability-aware write gate (lot A2). `/api/team` is mounted AFTER the
  // server's global read-only guard (server/index.js:519-525; router at :600)
  // → delete / merge / reorder 403 for a read-only account. (Editing one's OWN
  // card goes through the exempted `/api/me/team` — that stays open, on
  // AccountPage.) NOTE: a cap-holder's STRUCTURAL writes (POST/PUT) are
  // exempted from that guard by a dedicated pre-guard mount (server/index.js).
  const writeGate = useWriteGate(activeProfileId);
  const canManageTeam = authUser?.global_role === 'superadmin'
    || myBandRole === 'owner'
    || myBandRole === 'editor'
    || writeGate.canWriteCap('team.edit');
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !writeGate.canWrite;
  const [team, setTeam] = useState<TeamMemberDTO[]>([]);
  const [docs, setDocs] = useState<{ expired: ExpiringDoc[]; expiring: ExpiringDoc[] }>({ expired: [], expiring: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedBaseRoles, setSelectedBaseRoles] = useState<string[]>([]);
  const [scopeBand, setScopeBand] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [duplicates, setDuplicates] = useState<TeamDuplicateGroup[]>([]);
  // Pairs already declared "two different people" — shown so the owner can
  // REOPEN one (the pair then reappears in the detection).
  const [declaredNonDup, setDeclaredNonDup] = useState<{ members: TeamMemberDTO[] }[]>([]);
  const [merging, setMerging] = useState<string | null>(null);
    const [notDup, setNotDup] = useState<string | null>(null);
    const [reopenKey, setReopenKey] = useState<string | null>(null);
    const [dragIdx, setDragIdx] = useState<number | null>(null);

    // Manual reorder only makes sense when scoped to a single band (the order is
    // per-band on team_member_bands). When showing every band, keep the auto sort.
    // A NON-MANAGER is always band-scoped on purpose: the server then answers
    // with their own card only (2026-10-07), instead of the minimal projection
    // of every colleague.
    const scopedToBand = canManageTeam ? scopeBand : true;
    const bandId = scopedToBand && activeProfileId ? activeProfileId : null;
    // Drag-to-reorder is a manager gesture only.
    const dragBandId = canManageTeam ? bandId : null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    Promise.all([
      apiGetTeam(scopedToBand && activeProfileId ? { band: activeProfileId } : undefined),
      apiGetExpiringDocs(180).catch(() => ({ expired: [], expiring: [] })),
      apiGetTeamDuplicates().catch(() => [] as TeamDuplicateGroup[]),
      apiGetTeamNotDuplicates().catch(() => [] as { members: TeamMemberDTO[] }[]),
    ])
      .then(([members, d, dups, nonDups]) => {
        if (cancelled) return;
        setTeam(members);
        setDocs({ expired: d.expired || [], expiring: d.expiring || [] });
        setDuplicates(dups);
        setDeclaredNonDup(nonDups);
      })
      .catch((err) => {
        if (cancelled) return;
        // Surface the failure instead of rendering a silently empty page.
        setLoadError(err?.message || 'Failed to load team');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeProfileId, scopeBand, reloadKey]);

  const visible = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    return team
      .filter((m) => {
        if (selectedTags.length === 0) return true;
        // Category (musician/crew) is the first-class source of truth; role
        // tags no longer mirror it (2026-08-23), so always include it in the
        // tag-filter set so the musician/crew pills keep matching.
        const tags = new Set([m.category, ...(m.tags || [])]);
        return selectedTags.some((t) => tags.has(t));
      })
      .filter((m) => {
        if (selectedBaseRoles.length === 0) return true;
        const br = m.base_role || 'main';
        return selectedBaseRoles.includes(br);
      })
      .filter((m) => {
        if (!q) return true;
        return [m.name, m.nickname, m.email, m.phone, m.role, ...(m.tags || [m.category])]
          .filter(Boolean).join(' ').toLowerCase().includes(q);
      })
      .sort((a, b) => {
        // Manual per-band order primes; members without an explicit order fall
        // back to the automatic category/baseRole/name sort. (2026-09-02)
        const oa = a.order;
        const ob = b.order;
        if (oa != null && ob != null) return oa - ob;
        if (oa != null) return -1;
        if (ob != null) return 1;
        const ra = TAG_ORDER.indexOf(a.category); const rb = TAG_ORDER.indexOf(b.category);
        if (ra !== rb) return ra - rb;
        // Order within a category: base role first (main → session → guest),
        // then name. Matches the Musicians/Crew pages' sort. (2026-08-14)
        const brRank: Record<string, number> = { main: 0, session: 1, guest: 2 };
        const bara = brRank[a.base_role || 'main'] ?? 0;
        const barb = brRank[b.base_role || 'main'] ?? 0;
        if (bara !== barb) return bara - barb;
        return a.name.localeCompare(b.name);
      });
  }, [team, filterText, selectedTags, selectedBaseRoles]);

  const remove = useCallback(async (m: TeamMemberDTO) => {
    if (!writeGate.canWrite) return;
    // LOT A (2026-10-15) — the links are read from the card the user clicked,
    // which comes from GET /api/team and already carries `bands`. On a first
    // render they may be absent → say "unknown" rather than under-report: a
    // delete dialog that hides a band loss is exactly the incident we are fixing.
    let person: TeamMemberDTO = m;
    if (!Array.isArray(m.bands)) {
      try {
        const full = await apiGetTeamMember(m.id);
        person = { ...m, ...full };
      } catch {
        // keep `m` — buildPersonDeleteCopy prints the "unknown" line.
      }
    }
    if (!(await confirmPersonnelDelete(buildPersonDeleteCopy(person, t)))) return;
    try {
      await apiDeleteTeamMember(m.id);
      setReloadKey((k) => k + 1);
    } catch (err) {
      reportSaveError('team', err);
    }
  }, [writeGate.canWrite]);

  const alerts = [...docs.expired, ...docs.expiring];

  const mergePair = useCallback(async (canonical: TeamMemberDTO, dup: TeamMemberDTO) => {
    if (!writeGate.canWrite) return;
    if (!(await showConfirm(
      `Merge "${dup.name}" into "${canonical.name}"?\n\n` +
      'Passports, visas, rooming assignments and band links move to the kept ' +
      'person. The duplicate is removed and its id becomes an alias, so saving ' +
      'a rider will not recreate it.\n\nThis cannot be undone.',
    ))) return;
    setMerging(dup.id);
    try {
      await apiMergeTeamMembers(canonical.id, dup.id);
      setReloadKey((k) => k + 1);
    } catch (err) {
      reportSaveError('team', err);
    } finally {
      setMerging(null);
    }
  }, []);

  const markNotDuplicate = useCallback(async (canonical: TeamMemberDTO, dup: TeamMemberDTO) => {
    if (!writeGate.canWrite) return;
    setNotDup(dup.id);
    try {
      await apiMarkTeamNotDuplicate(canonical.id, dup.id);
      setReloadKey((k) => k + 1);
    } catch (err) {
      reportSaveError('team', err);
    } finally {
      setNotDup(null);
    }
  }, []);

  // Undo a "not a duplicate" declaration: the pair shows up again in the
  // detection above as soon as the reload completes.
  const reopenPair = useCallback(async (a: TeamMemberDTO, b: TeamMemberDTO) => {
    if (!writeGate.canWrite) return;
    if (!(await showConfirm(t('team.reopenConfirm', { a: a.name, b: b.name })))) return;
    const key = `${a.id}|${b.id}`;
    setReopenKey(key);
    try {
      await apiUndoTeamNotDuplicate(a.id, b.id);
      setReloadKey((k) => k + 1);
    } catch (err) {
      reportSaveError('team', err);
    } finally {
      setReopenKey(null);
    }
  }, [writeGate.canWrite, t]);

  // Definitive dismissal (SPEC-TEAM-ACCOUNT-REORDER): « These are 2 different
  // people » — persistent SERVER-side, the pair is never re-proposed (global,
  // no reopen). Open to any member with access to both people.
  const [dismissing, setDismissing] = useState<string | null>(null);
  const dismissPair = useCallback(async (a: TeamMemberDTO, b: TeamMemberDTO) => {
    const key = [a.id, b.id].sort().join('|');
    setDismissing(key);
    try {
      await apiDismissDuplicate(a.id, b.id);
      setReloadKey((k) => k + 1);
    } catch (err) {
      reportSaveError('team', err);
    } finally {
      setDismissing(null);
    }
  }, []);

  // Manual reorder (drag to reorder, like Musicians/Crew). Only enabled when
  // scoped to a single band — the order is per-band on team_member_bands.
  const reorderTeam = useCallback(
    (ids: string[]) => {
      if (!writeGate.canWrite) return;
      if (!bandId) return;
      // Optimistic local reorder: assign order = index to the band-scoped list.
      const orderMap = new Map(ids.map((id, i) => [id, i]));
      setTeam((prev) => prev.map((m) => (orderMap.has(m.id) ? { ...m, order: orderMap.get(m.id) } : m)));
      apiReorderTeam(bandId, ids).catch((err) => reportSaveError('team', err));
    },
    [bandId],
  );

  const handleDragStart = useCallback((e: React.DragEvent, idx: number) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIdx: number) => {
      e.preventDefault();
      if (dragIdx === null || dragIdx === dropIdx) return;
      const ids = visible.map((m) => m.id);
      const [moved] = ids.splice(dragIdx, 1);
      ids.splice(dropIdx, 0, moved);
      reorderTeam(ids);
      setDragIdx(null);
    },
    [dragIdx, visible, reorderTeam],
  );

  // LOT E1 — the activity card (ActivityPanel loads its own log) opens itself
  // when the log holds at least one action; the panel reports it upward.
  const [activityHasData, setActivityHasData] = useState(false);

  const TEAM_CARDS: StackCard[] = [
    // LOT E1 (2026-11-02): `hasData` = the card has something to show, so a
    // full card opens itself on a fresh view. `defaultOpen` is gone: it used
    // to open these three cards even when they rendered nothing.
    { id: 'duplicates', hasData: duplicates.length > 0, title: t('team.duplicatesTitle'), node: duplicates.length > 0 ? (<div className="card" style={{ borderColor: 'var(--info)', marginBottom: 18 }}>
  <strong style={{ color: 'var(--info)' }}>
    {t(duplicates.length > 1 ? 'team.possibleDup_other' : 'team.possibleDup', { count: duplicates.length })}
  </strong>
  <p style={{ color: 'var(--text-dim)', margin: '6px 0 10px', fontSize: '0.88rem' }}>
    {t('team.dupHint')} {t('team.dupeNoMerge')}
  </p>
  {duplicates.map((group) => {
    const head = group.members[0];
    const strong = group.confidence === 'strong';
    const badgeColor = strong ? 'var(--danger)' : 'var(--warning)';
    return (
      <div key={group.members.map((m) => m.id).sort().join('|')}
        style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 8 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{
            color: badgeColor, border: `1px solid ${badgeColor}`, borderRadius: 999,
            padding: '2px 10px', fontSize: '0.78rem', fontWeight: 600,
          }}>
            {t(strong ? 'team.dupeStrong' : 'team.dupeWeak')}
          </span>
          <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>
            {group.members.map((g) => `${g.name}${g.last_name ? ' ' + g.last_name : ''} — ${g.role} (${(g.bands || []).map((b) => b.name).join(', ') || t('team.noBand')})`).join('  ·  ')}
          </span>
        </div>
        {group.evidence.length > 0 && (
          <div style={{ color: 'var(--text-dim)', fontSize: '0.82rem', margin: '4px 0 6px' }}>
            <strong>{t('team.dupeEvidence')}</strong> {group.evidence.join('  ·  ')}
          </div>
        )}
        {group.members.slice(1).map((dup) => (
          <span key={dup.id} style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', marginRight: 10 }}>
            <button className="btn btn-small"
              disabled={merging === dup.id || !writeGate.canWrite}
              onClick={() => mergePair(head, dup)}>
              {merging === dup.id ? t('team.merging') : t('team.mergeIntoOne')}
            </button>
            <button className="btn btn-small btn-ghost"
              disabled={notDup === dup.id || !writeGate.canWrite}
              onClick={() => markNotDuplicate(head, dup)}>
              {notDup === dup.id ? t('team.marking') : t('team.notDuplicate')}
            </button>
            <button className="btn btn-small btn-ghost" data-dupe-dismiss
              disabled={dismissing === [head.id, dup.id].sort().join('|')}
              onClick={() => dismissPair(head, dup)}>
              {dismissing === [head.id, dup.id].sort().join('|') ? t('team.marking') : t('team.dupeDismiss')}
            </button>
          </span>
        ))}
      </div>
    );
  })}
</div>) : null },
    { id: 'alerts', hasData: alerts.length > 0, title: t('team.docsAttention', { count: alerts.length }), node: alerts.length > 0 ? (<div className="card" style={{ borderColor: 'var(--warning)', marginBottom: 18 }}>
  <strong style={{ color: 'var(--warning)' }}>
    {t(alerts.length > 1 ? 'team.docsAttention_other' : 'team.docsAttention', { count: alerts.length })}
  </strong>
  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
    {alerts.slice(0, 8).map((d, i) => {
      const left = daysUntil(d.expiry_date);
      const expired = left != null && left < 0;
      return (
        <span key={i} style={{ fontSize: '0.88rem' }}>
          <span style={{ color: expired ? 'var(--danger)' : 'var(--warning)' }}>
            {expired ? t('team.expired') : t('team.days', { count: left })}
          </span>
          {' — '}
          {d.member_name}: {d.kind === 'passport' ? t('team.passport') : t('team.visa', { country: d.country || '' })}
          {' '}({d.expiry_date})
        </span>
      );
    })}
  </div>
</div>) : null },
    { id: 'activity', hasData: canManageTeam && activityHasData, title: t('activity.title'), node: canManageTeam ? (<ActivityPanel
  bandId={bandId}
  canWrite={writeGate.canWrite}
  onChanged={() => setReloadKey((k) => k + 1)}
  onHasDataChange={setActivityHasData}
/>) : null },
  ];

  return (
    <div className="page" data-page="team" data-density="socle">
      <PageHeader
        title={withPageIcon('team.title', t('team.title'))}
        subtitle={t('team.subtitle')}
        actions={
          <button className="btn btn-primary" onClick={() => navigate('/tour/team/new')}>
            {t('team.addPerson')}
          </button>
        }
      />

      {readOnlyNote && (
        <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>
          {t('common.readOnlyAccount')}
        </p>
      )}

      <CardStack pageId="team" cards={TEAM_CARDS} foldable />

      

      {declaredNonDup.length > 0 && (
        <div className="card" style={{ borderColor: 'var(--border)', marginBottom: 18 }}>
          <strong>{t('team.dupeDeclared')}</strong>
          <p style={{ color: 'var(--text-dim)', margin: '6px 0 10px', fontSize: '0.88rem' }}>
            {t('team.dupeDeclaredHint')}
          </p>
          {declaredNonDup.map((pair) => {
            const [a, b] = pair.members;
            if (!a || !b) return null;
            const key = `${a.id}|${b.id}`;
            return (
              <div key={key} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                <span style={{ fontSize: '0.88rem' }}>
                  {a.name}{a.last_name ? ` ${a.last_name}` : ''}  ·  {b.name}{b.last_name ? ` ${b.last_name}` : ''}
                </span>
                <button className="btn btn-small btn-ghost"
                  disabled={reopenKey === key || !writeGate.canWrite}
                  onClick={() => reopenPair(a, b)}>
                  {reopenKey === key ? t('team.reopening') : t('team.reopen')}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {!canManageTeam && (
        <div className="card" style={{ borderColor: 'var(--info)', marginBottom: 18 }}>
          <strong>{t('team.yourCardTitle')}</strong>
          <p style={{ color: 'var(--text-dim)', margin: '6px 0 10px', fontSize: '0.9rem' }}>
            {t('team.yourCardHint')}
          </p>
          <button className="btn btn-primary btn-small" onClick={() => navigate('/account')}>
            {t('team.openMyAccount')}
          </button>
        </div>
      )}

      {loadError && (
        <div className="card" style={{ borderColor: 'var(--danger)', marginBottom: 18 }}>
          <strong style={{ color: 'var(--danger)' }}>{t('team.failedLoad')}</strong>
          <p style={{ color: 'var(--text-dim)', margin: '6px 0 10px' }}>{loadError}</p>
          <button className="btn btn-small" onClick={() => setReloadKey((k) => k + 1)}>{t('team.retry')}</button>
        </div>
      )}

      


      {canManageTeam && (
      <div className="filters" style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="form-input"
          style={{ maxWidth: 260 }}
          placeholder={t('team.searchPeople')}
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>{t('team.tags')}</span>
          <button
            className={`tag-pill ${selectedTags.length === 0 ? 'active' : ''}`}
            onClick={() => setSelectedTags([])}
            style={tagPillStyle(selectedTags.length === 0)}
          >
            {t('team.all')}
          </button>
          {TAG_ORDER.map((t) => (
            <button
              key={t}
              className={`tag-pill ${selectedTags.includes(t) ? 'active' : ''}`}
              onClick={() => setSelectedTags((prev) =>
                prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
              )}
              style={tagPillStyle(selectedTags.includes(t))}
            >
              {t}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>{t('team.baseRole')}</span>
          <button
            className={`tag-pill ${selectedBaseRoles.length === 0 ? 'active' : ''}`}
            onClick={() => setSelectedBaseRoles([])}
            style={tagPillStyle(selectedBaseRoles.length === 0)}
          >
            {t('team.all')}
          </button>
          {BASE_ROLE_ORDER.map((br) => (
            <button
              key={br}
              className={`tag-pill ${selectedBaseRoles.includes(br) ? 'active' : ''}`}
              onClick={() => setSelectedBaseRoles((prev) =>
                prev.includes(br) ? prev.filter((x) => x !== br) : [...prev, br],
              )}
              style={tagPillStyle(selectedBaseRoles.includes(br))}
            >
              {br}
            </button>
          ))}
        </div>
        {activeProfileId && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem', color: 'var(--text-dim)' }}>
            <input type="checkbox" checked={scopeBand} onChange={(e) => setScopeBand(e.target.checked)} />
            {t('team.thisBandOnly')}
          </label>
        )}
        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          {visible.length} / {team.length}
        </span>
      </div>
      )}

      {loading && <p style={{ color: 'var(--text-dim)' }}>{t('team.loading')}</p>}

      {!loading && visible.length === 0 && !loadError && (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <p style={{ color: 'var(--text-dim)' }}>
            {!canManageTeam
              ? t('team.noCardLinked')
              : team.length === 0
                ? t('team.noOneHere')
                : t('team.noMatch')}
          </p>
        </div>
      )}

      <div className="musician-grid">
        {visible.map((m, idx) => {
          const soonest = [...(m.passports || []), ...(m.visas || [])]
            .map((d) => daysUntil(d.expiry_date))
            .filter((d): d is number => d != null)
            .sort((a, b) => a - b)[0];
          // A non-manager only ever sees their OWN card: it opens Account (the
          // place where they may edit it), not the manager's edit page.
          const openCard = () => navigate(canManageTeam ? `/tour/team/${m.id}` : '/account');
          return (
            <div
              key={m.id}
              className={`musician-card${dragIdx === idx ? ' dragging' : ''}`}
              onDragOver={dragBandId ? handleDragOver : undefined}
              onDrop={dragBandId ? (e) => handleDrop(e, idx) : undefined}
              onDragEnd={dragBandId ? () => setDragIdx(null) : undefined}
            >
              <div className="musician-color-bar" style={{ backgroundColor: CATEGORY_COLORS[m.category] || '#a0a0b0' }} />
              {dragBandId && (
                <button
                  type="button"
                  className="musician-drag-handle"
                  draggable
                  onDragStart={(e) => handleDragStart(e, idx)}
                  onDragEnd={() => setDragIdx(null)}
                  title={t('musicians.dragToReorder')}
                  aria-label={t('musicians.dragToReorder')}
                >
                  ⋮⋮
                </button>
              )}
              <div
                className="musician-card-body"
                style={{ cursor: 'pointer' }}
                onClick={openCard}
              >
                <h3>{m.nickname || m.name}</h3>
                {(m.nickname && (m.real_name || m.last_name)) ? (
                  <p className="musician-role" style={{ fontSize: '0.85rem', marginTop: -6, opacity: 0.75 }}>
                    {[m.real_name, m.last_name].filter(Boolean).join(' ')}
                  </p>
                ) : null}
                <p className="musician-role">{m.category || t('team.noRole')}</p>
                <div className="musician-details" style={{ marginTop: 6, flexWrap: 'wrap', gap: 4 }}>
                  {(m.tags && m.tags.length ? m.tags.filter(t => t !== 'musician' && t !== 'crew') : []).map((t) => (
                    <span key={t} style={{
                      background: 'color-mix(in srgb, var(--text-muted) 14%, transparent)',
                      borderRadius: 999,
                      padding: '2px 10px',
                      fontSize: '0.72rem',
                      color: 'var(--text-dim)',
                    }}>
                      {t}
                    </span>
                  ))}
                </div>
                <div className="musician-details">
                  {m.email && <span>✉️ {m.email}</span>}
                  {m.phone && <span>📞 {m.phone}</span>}
                </div>
                <div className="musician-details">
                  {(m.passports?.length || 0) > 0 && <span>🛂 {t(m.passports!.length > 1 ? 'team.passports_other' : 'team.passports', { count: m.passports!.length })}</span>}
                  {(m.visas?.length || 0) > 0 && <span>📄 {t(m.visas!.length > 1 ? 'team.visas_other' : 'team.visas', { count: m.visas!.length })}</span>}
                  {soonest != null && soonest < 180 && (
                    <span style={{ color: soonest < 0 ? 'var(--danger)' : 'var(--warning)' }}>
                      {soonest < 0 ? t('team.expiredDoc') : t('team.expiresIn', { count: soonest })}
                    </span>
                  )}
                </div>
                {(m.bands?.length || 0) > 0 && (
                  <div className="musician-details">
                    {m.bands!.map((b) => <span key={b.id}>🎵 {b.name}</span>)}
                  </div>
                )}
                <div className="musician-card-actions">
                  {canManageTeam ? (
                    <>
                      <button className="btn btn-small" onClick={(e) => { e.stopPropagation(); navigate(`/tour/team/${m.id}`); }}>{t('team.edit')}</button>
                      <button className="btn btn-small btn-danger" disabled={!writeGate.canWrite} onClick={(e) => { e.stopPropagation(); remove(m); }}>{t('team.delete')}</button>
                    </>
                  ) : (
                    <button className="btn btn-small" onClick={(e) => { e.stopPropagation(); navigate('/account'); }}>{t('team.openMyAccount')}</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent actions + Undo (LOT B phase 1). Managers only: the journal is
          filtered server-side to bands the caller may manage, and a successful
          restore reloads the roster so the person reappears immediately. */}
      
    </div>
  );
}
